import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft, ArrowRight, Search, Calendar, Clock, User, Tag, 
  Newspaper, Lightbulb, Sparkles, Building2, ChevronRight, RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { SiteHeader, SiteFooter } from '../../components/website/SharedLayout';

// Default category icons mapping
const CATEGORY_ICONS = {
  'actualites': Building2,
  'conseils': Lightbulb,
  'tutoriels': Newspaper,
  'nouveautes': Sparkles,
  'default': Tag
};

const BlogPage = () => {
  const navigate = useNavigate();
  const { articleSlug } = useParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentArticle, setCurrentArticle] = useState(null);

  // Load articles and categories from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [articlesRes, categoriesRes] = await Promise.all([
          api.get('/website/blog/posts'),
          api.get('/website/blog/categories')
        ]);
        
        setArticles(articlesRes.data?.posts || []);
        setCategories(categoriesRes.data || []);
      } catch (error) {
        console.error('Error loading blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Load single article when slug changes
  useEffect(() => {
    const loadArticle = async () => {
      if (articleSlug) {
        try {
          const res = await api.get(`/website/blog/posts/${articleSlug}`);
          setCurrentArticle(res.data);
        } catch (error) {
          console.error('Article not found:', error);
          setCurrentArticle(null);
        }
      } else {
        setCurrentArticle(null);
      }
    };
    
    loadArticle();
  }, [articleSlug]);

  const filteredArticles = articles.filter(article => {
    const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
    const matchesSearch = !searchQuery || 
      article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (article.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const getCategoryInfo = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat || { name: 'Non classé', color: '#64748b' };
  };

  const getCategoryIcon = (categoryId) => {
    return CATEGORY_ICONS[categoryId] || CATEGORY_ICONS.default;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="flex items-center justify-center h-64 pt-24">
          <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
        <SiteFooter />
      </div>
    );
  }

  // Single Article View
  if (articleSlug && currentArticle) {
    const catInfo = getCategoryInfo(currentArticle.category);
    const relatedArticles = articles
      .filter(a => a.id !== currentArticle.id && a.category === currentArticle.category)
      .slice(0, 3);

    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />

        <article className="max-w-4xl mx-auto px-4 py-12 pt-24">
          {/* Category Badge */}
          <div className="mb-4">
            <Badge style={{ backgroundColor: catInfo.color, color: 'white' }}>
              {catInfo.name}
            </Badge>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            {currentArticle.title}
          </h1>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-8 pb-8 border-b">
            <div className="flex items-center gap-2">
              {currentArticle.author_avatar ? (
                <img src={getImageUrl(currentArticle.author_avatar)} alt={currentArticle.author} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white text-sm font-bold">
                  {(currentArticle.author || 'A').charAt(0)}
                </div>
              )}
              <span>{currentArticle.author || 'MSC L\'Agence'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(currentArticle.created_at)}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {currentArticle.read_time || 5} min de lecture
            </div>
          </div>

          {/* Featured Image */}
          {currentArticle.featured_image && (
            <div className="aspect-video rounded-2xl overflow-hidden mb-8">
              <img 
                src={getImageUrl(currentArticle.featured_image)}
                alt={currentArticle.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none mb-12"
            dangerouslySetInnerHTML={{ __html: currentArticle.content }}
          />

          {/* Tags */}
          {currentArticle.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-12">
              {currentArticle.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="bg-slate-100">
                  <Tag className="w-3 h-3 mr-1" />{tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Back Button */}
          <div className="border-t pt-8">
            <Button variant="outline" onClick={() => navigate('/site/blog')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Button>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-12 border-t">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Articles similaires</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {relatedArticles.map(article => {
                  const relCatInfo = getCategoryInfo(article.category);
                  return (
                    <Link 
                      key={article.id}
                      to={`/site/blog/${article.slug}`}
                      className="group"
                    >
                      <div className="aspect-video rounded-xl overflow-hidden mb-3">
                        {article.featured_image ? (
                          <img 
                            src={getImageUrl(article.featured_image)}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                            <Newspaper className="w-8 h-8 text-slate-300" />
                          </div>
                        )}
                      </div>
                      <Badge 
                        className="mb-2 text-white"
                        style={{ backgroundColor: relCatInfo.color }}
                      >
                        {relCatInfo.name}
                      </Badge>
                      <h3 className="font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </article>

        <SiteFooter />
      </div>
    );
  }

  // Blog List View
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 mb-4">
            Blog
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Actualités & Conseils
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Découvrez nos derniers articles, conseils marketing et actualités de l'agence
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveCategory('all')}
                className={activeCategory === 'all' ? 'bg-slate-900' : ''}
              >
                <Newspaper className="w-4 h-4 mr-2" />
                Tous
              </Button>
              {categories.map(cat => {
                const IconComponent = getCategoryIcon(cat.id);
                return (
                  <Button
                    key={cat.id}
                    variant={activeCategory === cat.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setActiveCategory(cat.id)}
                    style={activeCategory === cat.id ? { backgroundColor: cat.color } : {}}
                  >
                    <IconComponent className="w-4 h-4 mr-2" />
                    {cat.name}
                  </Button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-12">
              <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                {articles.length === 0 ? 'Aucun article publié' : 'Aucun article trouvé'}
              </h3>
              <p className="text-slate-500">
                {articles.length === 0 
                  ? 'Revenez bientôt pour découvrir nos prochains articles !'
                  : 'Essayez de modifier vos critères de recherche'
                }
              </p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredArticles.map(article => {
                const catInfo = getCategoryInfo(article.category);
                return (
                  <Link
                    key={article.id}
                    to={`/site/blog/${article.slug}`}
                    className="group"
                  >
                    <Card className="overflow-hidden h-full hover:shadow-xl transition-all duration-300 border-0 shadow-md">
                      {/* Image */}
                      <div className="aspect-video relative overflow-hidden">
                        {article.featured_image ? (
                          <img
                            src={getImageUrl(article.featured_image)}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            <Newspaper className="w-12 h-12 text-slate-300" />
                          </div>
                        )}
                        <Badge 
                          className="absolute top-3 left-3 text-white"
                          style={{ backgroundColor: catInfo.color }}
                        >
                          {catInfo.name}
                        </Badge>
                      </div>

                      <CardContent className="p-5">
                        <h3 className="font-bold text-lg text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                          {article.excerpt}
                        </p>

                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(article.created_at)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {article.read_time || 5} min
                            </span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-cyan-500 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default BlogPage;
